import { ApplyDiscountRequest } from 'models/Discount'
import { OrderType } from 'models/Order'
import { Proteins } from 'models/Pet'
import { Product, ProductTypes } from 'models/Product'
import { QueryClient, useQuery, UseQueryOptions } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { ApiClient } from 'services/ApiClient'
import { Api } from 'types/Api'
import { objectToQueryString } from 'utils/objectToQueryString'

export namespace FetchProducts {
  export type Response = Api.PaginatedResponse<Product, 'products'>
  export type Error = Api.Error
  export type Options = UseQueryOptions<Response, Error>
}

export type ProductRequest = {
  page: number
  limit: number
  proteins?: Proteins[]
  types?: ProductTypes[]
  textSearch?: string
  discount?: ApplyDiscountRequest
}

const createKey = (productsRequest: ProductRequest) => ['products', productsRequest]

const queryFetcher =
  ({
    discount = { orderType: OrderType.PRODUCTS_SUBSCRIPTION },
    ...productRequest
  }: ProductRequest) =>
  () => {
    const queryString = objectToQueryString({ ...productRequest })
    return ApiClient.post<FetchProducts.Response>(`/v1/products/preview${queryString}`, {
      data: { discount },
    })
  }

export const useFetchProducts = (
  productRequest: ProductRequest,
  options?: FetchProducts.Options
) => {
  return useQuery<FetchProducts.Response, FetchProducts.Error>(
    createKey(productRequest),
    queryFetcher(productRequest),
    options
  )
}

export const prefetchFetchProducts = async (
  productRequest: ProductRequest,
  options?: FetchProducts.Options
) => {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery<FetchProducts.Response, FetchProducts.Error>(
    createKey(productRequest),
    queryFetcher(productRequest),
    options
  )

  return {
    dehydratedState: dehydrate(queryClient),
  }
}
